// ═══════════════════════════════════════════════════════════════
// Hook: Importación masiva de empleados
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { employeeImportAPI } from '../api/employee-import';

const INITIAL_STATE = {
  step: 1,                    // 1: Descargar, 2: Subir, 3: Previsualizar, 4: Resultado
  importId: null,
  resumen: null,              // { total_filas, total_validos, total_warnings, total_errores, puede_importar }
  filas: [],                  // [{ fila, nombre, apellido, cedula, estado, errores[], warnings[] }]
  resultado: null,            // { importados, fallidos, total_intentados }
  loading: false,
  error: null,
};

export const useEmployeeBulkImport = () => {
  const [state, setState] = useState(INITIAL_STATE);

  const setLoading = (loading) => setState(s => ({ ...s, loading, error: null }));
  const setError = (error) => setState(s => ({ ...s, loading: false, error }));

  // ── Paso 1: Descargar plantilla ──────────────────────────────
  const downloadTemplate = useCallback(async () => {
    setLoading(true);
    try {
      await employeeImportAPI.downloadTemplate();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al descargar la plantilla.');
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  }, []);

  // ── Paso 2: Subir y validar archivo ─────────────────────────
  const uploadAndValidate = useCallback(async (file) => {
    setLoading(true);
    try {
      const data = await employeeImportAPI.validate(file);
      setState(s => ({
        ...s,
        loading: false,
        step: 3,
        importId: data.import_id,
        resumen: data.resumen,
        filas: data.filas,
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al procesar el archivo.');
    }
  }, []);

  // ── Paso 3: Confirmar importación ───────────────────────────
  const confirmImport = useCallback(async (modo = 'solo_validos') => {
    if (!state.importId) return;
    setLoading(true);
    try {
      const data = await employeeImportAPI.confirm(state.importId, modo);
      setState(s => ({
        ...s,
        loading: false,
        step: 4,
        resultado: data,
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al confirmar la importación.');
    }
  }, [state.importId]);

  // ── Navegación ───────────────────────────────────────────────
  const goToStep = useCallback((step) => {
    setState(s => ({ ...s, step, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    // Estado
    step: state.step,
    importId: state.importId,
    resumen: state.resumen,
    filas: state.filas,
    resultado: state.resultado,
    loading: state.loading,
    error: state.error,

    // Acciones
    downloadTemplate,
    uploadAndValidate,
    confirmImport,
    goToStep,
    reset,
  };
};

export default useEmployeeBulkImport;
